// SpeciesDetailsModal.js
import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { cancelReassessmentRequest, requestGetSiteSpecie } from 'api/siteSpecie'
import { SiteContext } from 'providers/site'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import SpeciesDetailsForm from './components/SpeciesDetailsForm'
import SpeciesDetailsAssessmentForm from './components/SpeciesDetailsAssessmentForm'
import Modal from 'components/Modal'
import FlowRequestModal from 'components/FlowRequestModal'
import { showErrorToast } from 'utils/toast'
import WarningModal from 'components/Modal/WarningModal'
import { ProfileContext } from 'providers/profile'
import { INDIVIDUAL_ROLE_ID } from 'constants/roles'
import { SITE_STATUS_EDIT_ONLY_ID, SITE_STATUS_SUBMITTED_EDIT_ONLY_ID } from 'constants/siteStatuses'

const SpeciesDetailsModal = ({ item, isOpen, setIsOpen, closeEditSpeciesModal }) => {
  const { profile } = useContext(ProfileContext)
  const isIndividualUser = profile.Role.id === INDIVIDUAL_ROLE_ID

  const params = useParams()
  const { site, siteProposalFieldValidation, setSiteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const [specie, setSpecie] = useState({ SpeciesAssesmentParameters: [] })
  const [selectedIds, setSelectedIds] = useState([])
  const [isWarningOpen, setWarningIsOpen] = useState(false)

  const editOnlySiteStatus = site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const historyId = editOnlySiteStatus ? site.sitehistoryId : params.siteHistoryId

  const specieId = item.reassessmentFlag ? item.historyId : item.id
  const checkFields = siteProposalFieldValidation.species[specieId]
    ? siteProposalFieldValidation.species[specieId]
    : defaultSiteProposalValidationResults.species

  function getSiteSpecie() {
    requestGetSiteSpecie(item.id, historyId, item.historyId).then((data) => {
      setSpecie(data)
      const warningCategories = ['A-NR', 'Q-NR', 'S-NR', 'U-NR', 'U-UR', 'A-UR']

      // Check if taxonTreatment is in the warning categories and show a warning
      if (warningCategories.includes(data.taxonTreatment)) {
        // If taxonTreatment matches the warning categories, show the warning modal
        setWarningIsOpen(true)
      }
    })
  }


  function deepEqual(obj1, obj2) {
    // Check if both objects are of type 'object'
    if (obj1 === null || obj2 === null || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
      // Get the keys of both objects
      const keys1 = Object.keys(obj1)
      const keys2 = Object.keys(obj2)

      // Check if the number of keys is the same
      if (keys1.length !== keys2.length) {
        return false
      }

      // Iterate through keys
      for (const key of keys1) {
        // Recursively compare nested properties
        if (!deepEqual(obj1[key], obj2[key])) {
          return false
        }
      }

      return true
    }
    // If not both objects, compare values directly
    return obj1 === obj2
  }

  function arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
      return false
    }

    for (let i = 0; i < arr1.length; i++) {
      if (!deepEqual(arr1[i], arr2[i])) {
        return false
      }
    }

    return true
  }

  const confirmAction = () => {
    if (!isIndividualUser) {
      showErrorToast('This action is not allowed.')
      return
    }
    cancelReassessmentRequest({ siteId: site.historyId, specieId: item.historyId, isCancelled: true }).then((data) => {
      if (data.error) {
        return showErrorToast(data.error)
      }
      setSiteProposalFieldValidation(defaultSiteProposalValidationResults)
      setIsOpen(false)
      closeEditSpeciesModal()
    })
  }

  useEffect(() => {
    getSiteSpecie()
  }, [])

  const handleUpdateChosenIds = (Ids) => {
    if (selectedIds && Ids) {
      if (!arraysEqual(selectedIds, Ids)) {
        setSelectedIds(Ids)
      }
    }
  }

  return (
    <div>
      <Modal
        width='416px'
        isOpen={isOpen}
        toggleModal={setIsOpen}
        modalTitleId='cancelReassessment'
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsOpen(false)
          }}
          confirmHandler={confirmAction}
          confirmBtnText='yesCancel'
          rejectBtnText='continueEditing'
          text={<FormattedMessage id='returnDiscardChangesText' values={{ specieName: <b>{item.scientificName}</b> }} />}
          item={item}
          approve={true}
        />
      </Modal>

      <WarningModal
        width='416px'
        isOpen={isWarningOpen}
        toggleModal={() => {
          return setWarningIsOpen(!isWarningOpen)
        }}
        modalTitleId='warningModalHeader'
        selectedIds={selectedIds}
      >
        <FlowRequestModal
          cancelHandler={() => {
            setWarningIsOpen(false)
          }}
          confirmHandler={confirmAction}
          confirmBtnText='yesCancel'
          rejectBtnText='continueEditing'
          text={<FormattedMessage id='warningModalExplanation' values={{ specieName: <b>{item.scientificName}</b> }} />}
          item={item}
          approve={true}
        />
      </WarningModal>

      <WarningModal isOpen={isWarningOpen} setIsOpen={setWarningIsOpen} text={<FormattedMessage id='warningModalExplanation' values={{ specieName: <b>{item.scientificName}</b> }} />} />

      {specie.scientificName && (
        <>
          <SpeciesDetailsForm
            item={specie}
            checkFields={checkFields}
            site={site}
            getSiteSpecie={getSiteSpecie}
            isEditable={isEditable}
          />
          <SpeciesDetailsAssessmentForm
            item={specie}
            getSiteSpecie={getSiteSpecie}
            specie={specie}
            setSpecie={setSpecie}
            setItem={setSpecie}
            checkFields={checkFields}
            site={site}
            setSiteProposalFieldValidation={setSiteProposalFieldValidation}
            isEditable={isEditable}
            updateIds={handleUpdateChosenIds}
          />
        </>
      )}
    </div>
  )
}

export default SpeciesDetailsModal
